// GalleryView.jsx

import React, { useCallback, useMemo } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { getSingularLabel } from 'utilities/Languages';
import { buildDetachEdgeLabel } from 'utilities/ViewRows';

// Services
import { deleteEdge } from 'services/nodes/NodeEdgeService';

// Hooks
import useGalleryCardFieldFormatting from 'views/gallery/hooks/useGalleryCardFieldFormatting';
import useGalleryMasonryLayout from 'views/gallery/hooks/useGalleryMasonryLayout';
import useGalleryPagination from 'views/gallery/hooks/useGalleryPagination';
import useDefaultNodeOpen from 'views/hooks/useDefaultNodeOpen';

// CSS
import './GalleryView.css';

// Components
import IconButton from 'components/iconbutton/IconButton';
import { toast } from 'react-toastify';
import GalleryCardRenderer from 'views/gallery/cards/GalleryCardRenderer';
import GalleryDataGrid from 'views/gallery/components/GalleryDataGrid';
import GalleryMediaGrid from 'views/gallery/components/GalleryMediaGrid';
import { writeNodeDragData } from 'views/dragDrop/nodeDragData';

/**
 * GalleryView
 * 
 * Renders a gallery view.
 *  
 *
 * @returns {ReactNode} - GalleryView.
 */
const GalleryView = React.memo(({
    enforceExplicitProperties = false,
    view,
    typeDef,
    nodes = [],
    parentNodeKey = null,
    parentNodeTypeLabel = '',
    language = 'en',
    maxNodes = null,
    onItemClick = null,
    addTypeKey,
    onQuickActionSelect,
    nodeDragEnabled = false,
}) => {

    // ----- CONTEXT ----- //

    // Theme Context

    const { theme } = useTheme();

    // ----- HANDLERS ----- //

    const handleOpenNode = useDefaultNodeOpen();

    // ----- HOOKS ----- //

    const {
        cardFieldKeys,
        showCover,
        showSubtitle,
        showNoteContents,
        schemaFieldMap,
        resolveFieldValue,
        formatFieldValue,
        getNodeFieldCount,
    } = useGalleryCardFieldFormatting({
        enforceExplicitProperties,
        view,
        typeDef,
        language,
        nodes,
    });

    const {
        displayedNodes,
        hasMore,
        loadMoreRef,
    } = useGalleryPagination({
        nodes,
        maxNodes,
        viewKey: view?.key,
    });

    // ----- MEMOS ----- //

    const isMediaType = useMemo(() => {
        const key = String(typeDef?.key || '').toUpperCase();
        return key === 'PHOTO' || key === 'VIDEO' || key === 'SKETCH';
    }, [typeDef?.key]);

    // ----- HANDLERS ----- //

    /**
 * Handles card click.
 *
 * @param {*} event
 * @param {*} node
 */

    const handleCardClick = useCallback((event, node) => {
        event?.preventDefault?.();
        if (typeof onItemClick === 'function') {
            event?.stopPropagation?.();
            onItemClick(node);
            return;
        }
        handleOpenNode(node);
    }, [handleOpenNode, onItemClick]);

    /**
 * Handles card double click.
 *
 * @param {*} event
 * @param {*} node
 */
    const handleCardDoubleClick = useCallback((event, node) => {
        event?.preventDefault?.();
        if (typeof onItemClick === 'function') {
            event?.stopPropagation?.();
            return;
        }
        handleOpenNode(node);
    }, [handleOpenNode, onItemClick]);

    /**
 * Handles detach node.
 *
 * @param {*} node
 */
    const handleDetachNode = useCallback(async (node) => {
        const parentKey = String(parentNodeKey || '').trim();
        const childKey = String(node?.key || '').trim();
        if (!parentKey || !childKey) return;

        try {
            await deleteEdge(parentKey, childKey);
        } catch (error) {
            toast.error(`Could not remove connection: ${error?.message || 'Unknown error'}`);
        }
    }, [parentNodeKey]);

    // ----- MEMOS ----- //

    const getDetachActionLabel = useCallback((node) => {
        const nodeTypeKey = String(node?.type || typeDef?.key || '').trim();
        const activeTypeKey = String(typeDef?.key || '').trim();
        const useActiveTypeDef = nodeTypeKey
            && activeTypeKey
            && nodeTypeKey.toUpperCase() === activeTypeKey.toUpperCase();
        const childNodeTypeLabel = useActiveTypeDef
            ? getSingularLabel(typeDef, nodeTypeKey, language)
            : nodeTypeKey;

        return buildDetachEdgeLabel({
            childNodeTypeLabel,
            parentNodeTypeLabel,
        });
    }, [language, parentNodeTypeLabel, typeDef]);

    const renderCardOverlay = useCallback((node) => {
        const childKey = String(node?.key || '').trim();
        if (!childKey) return null;

        const parentKey = String(parentNodeKey || '').trim();
        if (!parentKey) return null;

        return (
            <div
              className="gallery-view-card-overlay"
              onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                }}
              onDoubleClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                }}
            >
                <IconButton
                  iconKey="do_not_disturb_on"
                  iconSize={20}
                  title={getDetachActionLabel(node)}
                  onClick={(event) => {
                        event?.preventDefault?.();
                        event?.stopPropagation?.();
                        handleDetachNode(node);
                    }}
                />
            </div>
        );
    }, [getDetachActionLabel, handleDetachNode, parentNodeKey]);

    // ----- HOOKS ----- //

    const {
        containerRef,
        handleAspectRatio,
        gap,
        minCols,
        maxCols,
        colWidth,
    } = useGalleryMasonryLayout({
        displayedNodes,
        isMediaType,
        typeDef,
        getNodeFieldCount,
    });

    // ----- MEMOS ----- //

    const handleCardDragStart = useCallback((event, node) => {
        if (!nodeDragEnabled) return;
        writeNodeDragData(event, node);
    }, [nodeDragEnabled]);

    const renderCard = useCallback((item) => {

      return (
            <GalleryCardRenderer
              cardFieldKeys={cardFieldKeys}
              formatFieldValue={formatFieldValue}
              node={item}
              resolveFieldValue={resolveFieldValue}
              schemaFieldMap={schemaFieldMap}
              showCover={showCover}
              showSubtitle={showSubtitle}
              showNoteContents={showNoteContents}
              theme={theme}
              typeDef={typeDef}
              onAspectRatio={handleAspectRatio}
            />
        );
    }, [typeDef, cardFieldKeys, schemaFieldMap, resolveFieldValue, formatFieldValue, showCover, showSubtitle, showNoteContents, theme, handleAspectRatio]);

    const renderDataCard = renderCard;
    if (!isMediaType) {
        return (
            <GalleryDataGrid
              containerRef={containerRef}
              displayedNodes={displayedNodes}
              gap={gap}
              minCols={minCols}
              maxCols={maxCols}
              colWidth={colWidth}
              theme={theme}
              handleCardClick={handleCardClick}
              handleCardDoubleClick={handleCardDoubleClick}
              handleCardDragStart={nodeDragEnabled ? handleCardDragStart : null}
              renderDataCard={renderDataCard}
              renderCardOverlay={renderCardOverlay}
              hasMore={hasMore}
              loadMoreRef={loadMoreRef}
            />
        );
    }

    // ----- RENDER ----- //

    return (
        <GalleryMediaGrid
          containerRef={containerRef}
          theme={theme}
          displayedNodes={displayedNodes}
          gap={gap}
          minCols={minCols}
          maxCols={maxCols}
          colWidth={colWidth}
          handleCardClick={handleCardClick}
          handleCardDoubleClick={handleCardDoubleClick}
          handleCardDragStart={nodeDragEnabled ? handleCardDragStart : null}
          renderCard={renderCard}
          renderCardOverlay={renderCardOverlay}
          hasMore={hasMore}
          loadMoreRef={loadMoreRef}
        />
    );

});

// Dev Tools
GalleryView.displayName = 'GalleryView';

export default GalleryView;
